import './style.modules.scss'

import {
  BrandIcon,
  BrandIconColor,
  BrandIconId,
  LinkTarget,
  Notification,
  NotificationType
} from '@ampcore/adel-react'
import { AnalyticsTagBuilder } from '@dai-team/analytics-library'
import React, { useRef } from 'react'

import { AlertBackgroundColor } from './AlertBackgroundColor.type'
import { AlertMessageRecord } from './AlertMessageRecord.type'
import { AlertStatus } from './AlertStatus.type'
import { createAlertId } from './create-alert-id'
import { ALERT_STORAGE_KEY, useDismissedAlert } from './useDismissedAlert'

export type AlertProps = {
  icon?: BrandIconId
  iconColor?: BrandIconColor
  fill?: boolean
  stroke?: boolean
  body?: string
  bgColor?: AlertBackgroundColor
  heading?: string
  linkHref?: string
  linkTarget?: LinkTarget
  linkText?: string
  showCloseButton?: string // 'true' | '',
  analytics?: string
  psnAnalytics?: string
  notificationMessage: string
  testId?: string
}

export const Alert = (props: AlertProps) => {
  const {
    icon,
    iconColor,
    fill,
    stroke,
    body,
    bgColor,
    heading,
    linkHref,
    linkTarget,
    linkText,
    showCloseButton,
    analytics, // TODO rename analyticsName
    psnAnalytics,
    notificationMessage,
    testId
  } = props

  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const messageId = createAlertId(`${heading} + ${body}`)
  const atb = new AnalyticsTagBuilder()
    .setDefaultName('Alert')
    .setAnalyticsName(analytics)
    .setPsnAnalytics(psnAnalytics)

  const atb2 = new AnalyticsTagBuilder().setPsnAnalytics(psnAnalytics)

  const { isDismissed, setIsDismissed, showNotification } = useDismissedAlert(
    messageId,
    showCloseButton === 'true'
  )

  const handleUndoClick: (id: string) => void = id => {
    setIsDismissed(false)
    const record = localStorage.getItem(ALERT_STORAGE_KEY)
    if (record === null) {
      return
    }
    const messages = JSON.parse(record)
    messages.forEach((message: AlertMessageRecord, index: number) => {
      if (message.id === id) {
        messages.splice(index, 1)
      }
    })
    localStorage.setItem(ALERT_STORAGE_KEY, JSON.stringify(messages))
  }

  const handleCloseNotification: (closed: boolean) => void = closed => {
    if (closed) {
      const records = localStorage.getItem(ALERT_STORAGE_KEY)
      if (records === null) {
        return
      }
      const messagesData = JSON.parse(records)
      messagesData.forEach((message: AlertMessageRecord) => {
        if (message.id === messageId) {
          message.notification = AlertStatus.NOTIFICATION_HIDE
        }
      })
      localStorage.setItem(ALERT_STORAGE_KEY, JSON.stringify(messagesData))
    }
  }

  const classNames = []
  switch (bgColor) {
    case 'stone':
      classNames.push('u-bgColorStone')
      break
    case 'purple-light':
      classNames.push('u-bgColorPurpleLight10')
      break
    case 'lime-light':
      classNames.push('u-bgColorLimeLight20')
      break
    case 'mint-light':
      classNames.push('u-bgColorMintLight10')
      break
    default:
      classNames.push('u-bgColorStone')
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter') {
      closeButtonRef.current?.click()
    }
  }

  function handleCloseButton() {
    setIsDismissed(true)

    const storageRecord = localStorage.getItem(ALERT_STORAGE_KEY)
    let dismissedMessages = []
    if (storageRecord !== null) {
      dismissedMessages = JSON.parse(storageRecord)
    }

    const dismissedDateTime = new Date()
    dismissedMessages.push({
      id: messageId,
      date: dismissedDateTime.toISOString(),
      status: AlertStatus.CLOSED,
      notification: AlertStatus.NOTIFICATION_SHOW
    })
    localStorage.setItem(ALERT_STORAGE_KEY, JSON.stringify(dismissedMessages))
  }

  if (isDismissed && !showNotification) {
    return null
  }

  if (isDismissed && showNotification) {
    return (
      <Notification
        analytics={analytics ? `${analytics}-notification` : undefined}
        psnAnalytics={psnAnalytics}
        type={NotificationType.CONFIRM}
        dismissable={true}
        handleCloseNotification={handleCloseNotification}
        message={notificationMessage}
        testId={testId ? `${testId}-notification` : undefined}
      >
        <button
          type="button"
          className="Link u-inlineBlock u-marginLeft8"
          data-tracking="Button"
          onClick={() => handleUndoClick(messageId)}
          {...atb.dataAnalyticAttribute('notification-undo')}
          data-testid={testId ? `${testId}-notification-undo` : null}
        >
          Undo
        </button>
      </Notification>
    )
  }

  return (
    <section
      className={`Alert ${classNames.join(' ')}`}
      data-testid={testId ? `${testId}` : null}
    >
      <div className="u-sizeConstrained u-paddingVert20 u-sm-hidden u-md-hidden">
        <div className="u-flex u-flexAlignItemsStart">
          {icon && (
            <BrandIcon
              icon={icon}
              color={iconColor ? iconColor : BrandIconColor.NONE}
              fill={fill ? true : false}
              stroke={stroke ? true : false}
              className="Alert-icon u-marginRight24"
              style={{ height: '36px', width: '36px' }}
              testId={`${testId}-desktop-icon`}
            />
          )}
          <div className="u-flex u-flexCol">
            <h3
              className="u-textColorNeutralGrey u-flexShrink0"
              data-testid={testId ? `${testId}-heading` : null}
            >
              {heading}
            </h3>
            {body && (
              <div
                className="Content u-textColorNeutralGrey u-marginTop20"
                dangerouslySetInnerHTML={{ __html: body }}
                data-testid={testId ? `${testId}-body` : null}
              ></div>
            )}
          </div>
          <div className="u-flex u-flexJustifyCenter u-flexAlignItemsCenter u-flexExpandLeft u-flexShrink0">
            {linkHref && (
              <a
                className="Alert-button Button Button--small Button--secondary Button--secondary--action-blue u-marginHoriz24"
                href={linkHref}
                target={linkTarget}
                {...atb2.dataAnalyticAttribute(analytics)}
                data-testid={testId ? `${testId}-link` : null}
              >
                {linkText}
              </a>
            )}
            {showCloseButton && (
              <button
                title="Close"
                aria-label="Close"
                ref={closeButtonRef}
                tabIndex={0}
                className="Button Button--icon Alert-closeIcon u-textColorNeutral"
                data-testid={testId ? `${testId}-close-button` : null}
                onClick={() => {
                  handleCloseButton()
                }}
                onKeyDown={handleKeyDown}
                {...atb.dataAnalyticAttribute('close')}
              >
                close
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="u-sizeConstrained u-paddingVert20 u-lg-hidden">
        <div className="u-flex u-flexCol u-alignItemsBaseline">
          <div className="u-flex u-flexAlignItemsStretch">
            {icon && (
              <BrandIcon
                icon={icon}
                color={iconColor ? iconColor : BrandIconColor.NONE}
                fill={fill ? true : false}
                stroke={stroke ? true : false}
                className="Alert-icon u-marginRight20"
                style={{ height: '36px', width: '36px' }}
                testId={`${testId}-mobile-icon`}
              />
            )}
            <h3 className="u-textColorNeutralGrey u-marginRight20">
              {heading}
            </h3>
            {showCloseButton && (
              <div className="u-flexExpandLeft">
                <button
                  title="Close"
                  aria-label="Close"
                  ref={closeButtonRef}
                  className="Button Button--icon u-textColorNeutral"
                  onClick={() => {
                    handleCloseButton()
                  }}
                  onKeyDown={handleKeyDown}
                  {...atb.dataAnalyticAttribute('close')}
                >
                  close
                </button>
              </div>
            )}
          </div>
          {body && (
            <div
              className="Content u-marginVert20 u-textColorNeutralGrey"
              dangerouslySetInnerHTML={{ __html: body }}
            ></div>
          )}
          {linkHref && (
            <a
              className="Alert-button Button Button--secondary Button--secondary--action-blue"
              href={linkHref}
              target={linkTarget}
              data-analytics={analytics}
            >
              {linkText}
            </a>
          )}
        </div>
      </div>
    </section>
  )
}
